import React, { useEffect, useState } from "react";
import { ExternalLink, Tag } from "lucide-react";
import { api } from "../../lib/api";

/**
 * AffiliateKitBox — small "Outdoor kit · partner deals" panel that lists
 * each enabled partner brand with a single tracked CTA per brand.
 *
 * Mounted on:
 *   • CampsiteDetail (sidebar position, optional)
 *   • Anywhere else a low-friction sidebar nudge makes sense
 *
 * Rendering rules:
 *   • Tiny † superscript next to each brand for ASA disclosure
 *   • One disclosure line at the bottom
 *   • All links go through /api/r/{slug} so we capture every click
 */
export default function AffiliateKitBox({ title = "Outdoor kit · partner deals" }) {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/affiliate/brands")
      .then(({ data }) => { if (alive) setBrands(data.brands || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading || brands.length === 0) return null;

  return (
    <div className="border hairline bg-[#fffdfa] p-4 mb-6" data-testid="affiliate-kit-box">
      <div className="flex items-center gap-2 mb-3">
        <Tag className="w-4 h-4 text-[#ff4500]" strokeWidth={1.7} />
        <div className="eyebrow">{title}</div>
      </div>
      <ul className="space-y-2.5">
        {brands.map((b) => (
          <li key={b.slug} className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <a
                href={`/api/r/${b.slug}`}
                target="_blank"
                rel="sponsored noopener"
                className="font-display text-base text-[#111] hover:text-[#ff4500] inline-flex items-center gap-1"
                data-testid={`aff-${b.slug}`}
              >
                {b.name}
                <sup className="text-[#ff4500] text-[0.6em] opacity-75" title="Partner link — we earn a small commission. Costs you nothing.">†</sup>
                <ExternalLink className="w-3 h-3 ml-0.5 text-[#aaa]" />
              </a>
              {b.category && (
                <div className="mono text-[10px] uppercase tracking-wider text-[#888] mt-0.5">{b.category}</div>
              )}
              {b.blurb && (
                <p className="text-[12px] text-[#555] leading-snug mt-1">{b.blurb}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
      <p className="text-[10px] text-[#888] leading-snug mt-3 pt-3 border-t hairline">
        <sup>†</sup> Partner links — CampCompass UK earns a small commission if you buy.
        Costs you nothing and helps keep the app free of ads.
      </p>
    </div>
  );
}
